import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ReportIcon from '@mui/icons-material/Report';
import { Box, Typography } from '@mui/material';
import { FC } from 'react';

import { PieceAssetSize } from '@apiContract';

import { getPieceAsset } from '@utils/piece';

import { DiscreteScanResult, ScanState, normalize } from '../hooks/useQrScanFeedback';
import {
  sxScanFeedbackCard,
  sxScanFeedbackDetails,
  sxScanFeedbackHeader,
  sxScanFeedbackImage,
  sxScanFeedbackMain,
  sxScanFeedbackValue,
} from '../styles';

const RETRY_HINT = 'Try scanning all remaining codes on the item, or validate it with an image.';

const ERROR_CONTENT: Partial<Record<ScanState, { title: string; description: string }>> = {
  [ScanState.NotIncluded]: {
    title: 'Tracking number not included',
    description: `This tracking number is not associated with any of the selected items. ${RETRY_HINT}`,
  },
  [ScanState.ImproperFormat]: {
    title: 'Tracking number has improper format',
    description: `This tracking number has an improper format and cannot be used for validation. ${RETRY_HINT}`,
  },
  [ScanState.NotUnique]: {
    title: 'Tracking number not unique',
    description: `This tracking number is associated with multiple active items. ${RETRY_HINT}`,
  },
};

type ScanFeedbackCardProps = {
  result: DiscreteScanResult;
  remainingCount: number;
  trackingNumbers?: string[];
};

export const ScanFeedbackCard: FC<ScanFeedbackCardProps> = ({ result, remainingCount, trackingNumbers = [] }) => {
  const { state, piece } = result;

  const errorContent = ERROR_CONTENT[state];
  const color = errorContent ? 'error' : 'success';
  const Icon = errorContent ? ReportIcon : CheckCircleRoundedIcon;
  const title = errorContent?.title ?? (state === ScanState.Duplicate ? 'Item already validated' : 'Item validated');

  const imageUrl = getPieceAsset(piece?.pieceAssets || [], PieceAssetSize.Medium)?.url || '/images/empty_thumbnail.png';

  const isScanned = (trackingNumber: string) => normalize(trackingNumber) === normalize(result.code);
  const orderedTrackingNumbers = [...trackingNumbers].sort((a, b) => Number(isScanned(b)) - Number(isScanned(a)));

  return (
    <Box sx={sxScanFeedbackCard(color)}>
      <Box sx={sxScanFeedbackHeader(color)}>
        <Icon color={color} />
        <Box>
          <Typography variant="h4" color="common.white">
            {title}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {errorContent?.description ?? `${remainingCount} remaining. Waiting for next scan...`}
          </Typography>
        </Box>
      </Box>

      <Box sx={sxScanFeedbackMain}>
        {errorContent ? (
          <Box sx={sxScanFeedbackDetails}>
            <Typography variant="body2" color="text.secondary">
              Scanned
            </Typography>
            <Typography variant="h5" title={result.code} sx={sxScanFeedbackValue(false)}>
              {result.code}
            </Typography>
          </Box>
        ) : (
          <>
            <Box component="img" src={imageUrl} alt={`Piece ${piece?.id}`} sx={sxScanFeedbackImage} />

            <Box sx={sxScanFeedbackDetails}>
              <Typography variant="body2" color="text.secondary">
                Tracking numbers
              </Typography>
              <Box sx={{ mb: 2 }}>
                {orderedTrackingNumbers.length ? (
                  orderedTrackingNumbers.map((trackingNumber) => (
                    <Typography
                      variant="h5"
                      key={trackingNumber}
                      title={trackingNumber}
                      sx={sxScanFeedbackValue(isScanned(trackingNumber))}
                    >
                      {trackingNumber}
                    </Typography>
                  ))
                ) : (
                  <Typography variant="h5" sx={sxScanFeedbackValue(false)}>
                    —
                  </Typography>
                )}
              </Box>

              <Typography variant="body2" color="text.secondary">
                Recipient
              </Typography>
              <Typography
                variant="h5"
                title={piece?.recipient?.displayName || undefined}
                sx={sxScanFeedbackValue(false)}
              >
                {piece?.recipient?.displayName || '—'}
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
